/**
 * Dashboard Repository
 * Handles dashboard-related API calls
 */

import axios, { AxiosInstance } from 'axios'
import API_CONFIG from '../config/api.config'
import authRepository from './auth.repository'

export interface CountryAnalytic {
  country: string
  countryTotalTrades: number
  countryActiveTrades: number
  countryLotsVolume: number
  countryLotsSharePercent: number
  countryNetProfit: number
  countryAvgLotsPerTrade: number
}

export interface GeographicIntelligenceData {
  totalTrades: number
  totalLotsVolume: number
  avgLotsPerTrade: number
  totalNetProfit: number
  activeTrades: number
  liveTradeAccounts: number
  countryAnalytics: CountryAnalytic[]
  demoTradeAccounts: number
  totalTradeAccounts: number
  liveAccountsPercent: number
  demoAccountsPercent: number
}

class DashboardRepository {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      config => {
        const authHeaders = authRepository.getAuthHeader()
        if (authHeaders) {
          config.headers = { ...config.headers, ...authHeaders }
        }
        return config
      },
      error => Promise.reject(error)
    )

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          authRepository.clearAuthData()
          window.location.href = '/broker/login'
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Get geographic intelligence data
   */
  async getGeographicIntelligence(startDate: Date, endDate: Date, country?: string): Promise<GeographicIntelligenceData> {
    try {
      const params: any = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }

      if (country) {
        params.country = country.toUpperCase()
      }

      console.log('Fetching geographic intelligence data:', {
        url: API_CONFIG.DASHBOARD.GEOGRAPHIC_INTELLIGENCE,
        params
      })

      const response = await this.api.get<GeographicIntelligenceData>(
        API_CONFIG.DASHBOARD.GEOGRAPHIC_INTELLIGENCE,
        { params }
      )

      console.log('Geographic intelligence response:', response.data)
      return response.data
    } catch (error) {
      console.error('Failed to fetch geographic intelligence data:', error)
      throw error
    }
  }

  /**
   * Get geographic data formatted for map display
   */
  async getMapData(startDate: Date, endDate: Date): Promise<Map<string, { volume: number; percentage: number }>> {
    try {
      const data = await this.getGeographicIntelligence(startDate, endDate)
      const mapData = new Map<string, { volume: number; percentage: number }>()

      // Transform country analytics for map display
      data.countryAnalytics.forEach(country => {
        // Normalize country names to match map expectations
        const countryName = this.normalizeCountryName(country.country)
        mapData.set(countryName.toLowerCase(), {
          volume: Math.round(country.countryLotsVolume),
          percentage: Math.round(country.countryLotsSharePercent)
        })
      })

      return mapData
    } catch (error) {
      console.error('Failed to get map data:', error)
      throw error
    }
  }

  /**
   * Get dashboard summary stats (for the three cards)
   */
  async getDashboardStats(startDate: Date, endDate: Date): Promise<{
    totalTraders: number
    activeTraders: number
    totalVolume: number
    liveAccountsPercent: number
    demoAccountsPercent: number
  }> {
    try {
      const data = await this.getGeographicIntelligence(startDate, endDate)

      return {
        totalTraders: data.totalTradeAccounts,
        activeTraders: data.liveTradeAccounts,
        totalVolume: Math.round(data.totalLotsVolume),
        liveAccountsPercent: Math.round(data.liveAccountsPercent),
        demoAccountsPercent: Math.round(data.demoAccountsPercent)
      }
    } catch (error) {
      console.error('Failed to get dashboard stats:', error)
      throw error
    }
  }

  /**
   * Normalize country names to match SVG map component ISO codes
   */
  private normalizeCountryName(country: string): string {
    const nameMap: { [key: string]: string } = {
      'Argentina': 'ar',
      'ARGENTINA': 'ar',
      'BOLIVIA': 'bo',
      'Bolivia': 'bo',
      'CANADA': 'ca',
      'Canada': 'ca',
      'Colombia': 'co',
      'COLOMBIA': 'co',
      'COSTA RICA': 'cr',
      'Costa Rica': 'cr',
      'ECUADOR': 'ec',
      'Ecuador': 'ec',
      'GERMANY': 'de',
      'Germany': 'de',
      'GUATEMALA': 'gt',
      'Guatemala': 'gt',
      'INDIA': 'in',
      'India': 'in',
      'INDONESIA': 'id',
      'Indonesia': 'id',
      'KENYA': 'ke',
      'Kenya': 'ke',
      'MALAYSIA': 'my',
      'Malaysia': 'my',
      'MEXICO': 'mx',
      'Mexico': 'mx',
      'NIGERIA': 'ng',
      'Nigeria': 'ng',
      'PANAMA': 'pa',
      'Panama': 'pa',
      'PHILIPPINES': 'ph',
      'Philippines': 'ph',
      'SOUTH AFRICA': 'za',
      'South Africa': 'za',
      'THAILAND': 'th',
      'Thailand': 'th',
      'UNITED ARAB EMIRATES': 'ae',
      'United Arab Emirates': 'ae',
      'United States of America': 'us',
      'USA': 'us',
      'UZBEKISTAN': 'uz',
      'Uzbekistan': 'uz',
      'VIET NAM': 'vn',
      'Vietnam': 'vn',
      'Viet Nam': 'vn'
    }

    // Try to find exact match first
    const code = nameMap[country]
    if (code) return code

    // Try case-insensitive match
    const upperCountry = country.toUpperCase()
    for (const [key, value] of Object.entries(nameMap)) {
      if (key.toUpperCase() === upperCountry) {
        return value
      }
    }

    // Default to first two letters of country name lowercased
    return country.substring(0, 2).toLowerCase()
  }
}

export default new DashboardRepository()